import React from 'react';
import { NumberInput, NumberInputProps } from '@mantine/core';
import { toMantineError } from '../../theme/forms';
import { describedByContainer } from '../../theme/describedByContainer';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { IInputProps } from '../Input/Input';

// Фасад на Mantine NumberInput. Публичный API (IInputNumberProps) заморожен ради
// потребителей (InputNumberControl ×13, EditTable ×6, движок форм Field/Calc): старые
// пропы separator/floatPoints/groupBy/min/max/onValueChange маппятся в нативные NumberInput
// внутри. Самодельный парсер (numberWithSpaces), скрытый зеркальный <input> и синтетический
// dispatchEvent('change') сняты — форматирование/группировка/кламп делает NumberInput нативно.
// Mantine-aligned переименования (decimalScale/decimalSeparator/thousandSeparator) — в общий
// codemod кластера позже, как и у Input. См. PROPS-AUDIT «InputNumber».
export interface IInputNumberProps extends IInputProps {
    /** Начальное/внешнее числовое значение (ведёт отображение, ресинкается при изменении). */
    defaultValue?: string | number;
    /** Разделитель групп разрядов (legacy). При groupBy===0 группировки нет. @default ' ' */
    separator?: string;
    /** Скрыть кнопки-степпер (вверх/вниз) справа. @default false */
    hideControls?: boolean;
    /**
     * Число знаков после запятой: дробная часть дополняется нулями до него. 0 ⇒ только целые. `'any'` — число
     * произвольной точности: дробная часть любой длины, как введена, без дополнения нулями.
     * @default 0
     */
    floatPoints?: number | 'any';
    /** Шаг степпера/стрелок/колёсика. @default 10^(-floatPoints) — 1 при 0 и `'any'`, 0.01 при 2. */
    step?: number;
    /** Размер группы разрядов (legacy): 0 ⇒ без группировки. */
    groupBy?: number;
    /** Максимум (кламп на лету). */
    max?: number;
    /** Минимум (кламп на лету). */
    min?: number;
    /**
     * Значение на каждый ввод и при клампе внешнего — строкой (потребители делают `+value.replace(/\s/g,'')`).
     * Одноимённый колбэк Mantine `NumberInput` (react-number-format) не прокидывается.
     */
    onValueChange?: (value: string) => void;
    /**
     * @deprecated Легаси-testid: рендерит data-testid="inputnubmer-${testid}" (опечатка
     * сохранена для e2e-паритета). Уйдёт в едином testid→data-testid пассе.
     */
    testid?: string;
    /** Задержка до автоповтора шага при удержании кнопки степпера, мс. @default 500 */
    stepHoldDelay?: number;
    /**
     * Интервал автоповтора при удержании: число (мс) или функция от количества уже
     * сделанных шагов. @default ускоряющийся 1000/n² с полом 25мс
     */
    stepHoldInterval?: number | ((stepCount: number) => number);
}

/** Нормализация входа к number | '' (для seed/сравнения; запятая→точка, пробелы прочь). */
const parseNum = (v: string | number | null | undefined): number | '' => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(String(v).replace(',', '.').replace(/\s/g, ''));
    return Number.isNaN(n) ? '' : n;
};

// Ускоряющийся автоповтор шага при удержании кнопки степпера (значения из примера
// mantine.dev/core/number-input): первый повтор через stepHoldDelay, дальше интервал
// сокращается с числом сделанных шагов (1000мс → … → пол 25мс).
const acceleratingStepHoldInterval = (stepCount: number): number => Math.max(1000 / stepCount ** 2, 25);

// Кламп ВНИЗ к [min; max]: значение за границей подтягивается к границе, в границах — исходная
// форма (строка вроде «9.00») сохраняется. Нечисловой/частичный ввод возвращается как есть.
const clampToBounds = (raw: string | number, min?: number, max?: number): string | number => {
    const n = parseNum(raw);
    if (typeof n !== 'number') return raw;
    if (max !== undefined && n > max) return max;
    if (min !== undefined && n < min) return min;
    return raw;
};

const InputNumber: React.FC<IInputNumberProps> = ({
    ref,
    max,
    min,
    defaultValue = '',
    separator,
    hideControls = false,
    floatPoints = 0,
    step,
    groupBy,
    onValueChange,
    invalid,
    onKeyDown,
    size = 'sm',
    testid,
    stepHoldDelay = 500,
    stepHoldInterval = acceleratingStepHoldInterval,
    // Гасим из ...rest пропы, которые АКТИВНО сломали бы NumberInput (их передают потребители):
    // type — Calc шлёт type="number", а react-number-format под NumberInput требует text/tel.
    // value/onChange задаём сами ниже (controlled), потребители их в InputNumber не передают.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    type: _type,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    inputContainer,
    ...rest
}) => {
    const fieldA11y = useFieldA11y(
        { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        !!rest.label,
    );
    // Контролируемое значение: NumberInput может вернуть строку для edge-кейсов (пусто,
    // хвостовая точка «1.», очень большое число) — храним как есть, не теряя ввод.
    const [value, setValue] = React.useState<number | string>(() => parseNum(defaultValue));

    // Ресинк от внешнего defaultValue (RHF-значение, вычисления Calc), НО с ЧИСЛОВЫМ
    // сравнением: эхо от Control (onValueChange → onChange → defaultValue) при равном
    // числовом значении не должно затирать промежуточный ввод вроде «1.» при наборе дроби.
    // Внешнее значение клампится к min/max с пробросом в onValueChange (легаси-эффект на
    // [value] делал то же): для числовых полей движка форм кламп компонента — единственный
    // enforcement границ (RHF-правила min/max строятся только для текстовых типов).
    React.useEffect(() => {
        const incoming = parseNum(defaultValue);
        const next = clampToBounds(incoming, min, max);
        setValue((prev) => (next === parseNum(prev) ? prev : next));
        if (next !== incoming) onValueChange?.(String(next));
    }, [defaultValue]);

    // groupBy===0 ⇒ группировки нет; иначе разделитель из separator (по умолчанию пробел,
    // паритет numberWithSpaces). Mantine группирует по 3 разряда (thousandsGroupStyle).
    const thousandSeparator = groupBy === 0 ? undefined : (separator ?? ' ');

    // Шаг стрелок/колёсика привязан к отображаемой точности: один «клик» меняет последний
    // видимый разряд (floatPoints=2 → 0.01, =0 → 1). Явный step потребителя перекрывает дефолт.
    const anyPrecision = floatPoints === 'any';
    const decimals = anyPrecision ? 0 : floatPoints;
    const resolvedStep = step ?? (decimals > 0 ? 10 ** -decimals : 1);

    const handleChange: NumberInputProps['onChange'] = (val) => {
        // Кламп ВНИЗ на лету (паритет старого useEffect-клампа): значение за границей
        // подтягивается к max/min, а не блокируется (как Mantine clampBehavior='strict')
        // и не остаётся за границей до blur (как 'blur'). Сравниваем по ЧИСЛОВОЙ
        // интерпретации: при fixedDecimalScale Mantine отдаёт onChange СТРОКУ ('9.00'),
        // и кламп по typeof number её бы пропустил (дробные рейтинги/Money не клампились).
        // Пустой/частичный ввод (parseNum → '') не клампим — даём набирать и очищать.
        const next = clampToBounds(val, min, max);
        setValue(next);
        // onValueChange — легаси-контракт: строка. Потребители (Control, EditTable) делают
        // +value.replace(/\s/g,''); Mantine отдаёт уже число — String() достаточно.
        onValueChange?.(next === '' || next === null || next === undefined ? '' : String(next));
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
        }
        onKeyDown?.(event);
    };

    return (
        <NumberInput
            {...(rest as NumberInputProps)}
            aria-label={ariaLabel}
            aria-labelledby={fieldA11y['aria-labelledby']}
            inputContainer={describedByContainer(fieldA11y['aria-describedby'], inputContainer)}
            ref={ref}
            size={size}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            error={toMantineError(invalid)}
            max={max}
            min={min}
            // Кламп делаем сами в handleChange (вниз, на лету); нативный Mantine-кламп
            // выключен: 'strict' блокировал бы ввод за границей, 'blur' клампил бы только на blur.
            clampBehavior="none"
            step={resolvedStep}
            decimalScale={anyPrecision ? undefined : decimals}
            fixedDecimalScale={decimals > 0}
            allowDecimal={anyPrecision || decimals > 0}
            allowNegative
            decimalSeparator="."
            allowedDecimalSeparators={[',']}
            thousandSeparator={thousandSeparator}
            // Степпер (кнопки вверх/вниз) по умолчанию включён; hideControls — опт-аут.
            hideControls={hideControls}
            stepHoldDelay={stepHoldDelay}
            stepHoldInterval={stepHoldInterval}
            {...(testid ? { 'data-testid': `inputnubmer-${testid}` } : {})}
        />
    );
};

export default InputNumber;
